// ui/explore.js -- the sidebar's home: the explore view (spec 0061 req 2 and 3, design §2).
//
// Contract: createExplore(ctx, host) -> { root, tab(), setTab(id), refresh(), mountTab(id, render, opts),
//           next (ui/next.js's list), todayHost (ui/today.js's seat), crew() }
// Also exported, pure, for tests/test_shell.mjs:
//   TABS, tabTarget(tab) -> {stage, moment}, tabFor(stageId, moment) -> tab
//   rightNowLines({storms, aurora, clouds, crewed, wallMs}) -> at most three {id, text, value, lead}
//   statusSummary(rows, wallMs) -> {state, text}
//   tripMeta(row) -> the line under a trip card's title
//
// WHAT IT REPLACES. The left column was a settings list wrapped around the globe: a trips drop-down
// with its paragraph, one orange button, the Wonder / Now / Next doors, fourteen checkboxes, a scale
// ladder, the clock, a place and a search box, 3 784 px of it in a 496 px window. Row D keeps what
// answers "what is there to look at" and moves the rest out: the layers to the tool rail
// (ui/rail.js), the clock to the pill (ui/timepill.js), the sources to their sheet (ui/status.js).
//
//   Space Radar                               ‹       the wordmark; ‹ collapses to the handle
//   [ Search planets, stars, satellites     ]         ui/search.js, mounted here
//   [ Earth | Planets | Stars | Tonight ]             places, not settings: a tab flies there
//   RIGHT NOW   at most three lines from live state, a missing one left out, never filler
//   TRIPS       ui/trippicker.js's data as cards, two columns; a tap starts it
//   COMING UP   ui/next.js, five rows, "Show all"
//   ● 21 read this visit · oldest 6 days ago          the status line; opens the sources sheet
//
// TABS ARE PLACES (req 3). Earth is the Earth stage; Planets the Sun's, the worlds as a list; Stars
// the stellar rung, the ladder's far places and the star systems; Tonight the sky from your place,
// which is the old Now moment. The old moments map onto them -- Wonder is the first three, Now is
// Tonight, Next is "Coming up" -- so main.js's setMoment and its per-moment layer defaults are
// unchanged. The tab follows the stage too: a search that flies to Sirius lights Stars, because a
// control that disagrees with the scene is a control that lies (the doors did, 2026-09-21).
//
// No paragraph of explanation anywhere (req 2). A line that needs one belongs to a card.

import { COPY, t, fmt, ageInWords } from '../copy/en.js';
import { STAGES, isLadderStage, isSystemStage } from '../scene/stage.js';
import { TOUR_GROUPS } from '../data/tours-index.js';
import { LADDER_RUNGS, WE_SHOW } from '../data/ladder.js';
import { SYSTEMS } from '../data/systems.js';
import { load } from '../data/sources.js';
import { parseAstros } from '../data/parsers.js';
import { groupTrips, eventSubtitle } from './trippicker.js';
import { tripPicture } from './trippics.js';
import { createSearch } from './search.js';
import { createNext, auroraItem } from './next.js';
import { tagLines } from './cardgate.js';
import { revealInColumn } from './reveal.js';

export const TABS = ['earth', 'planets', 'stars', 'tonight'];
const TARGETS = {
  earth: { stage: 'earth', moment: 'wonder' },
  planets: { stage: 'sun', moment: 'wonder' },
  stars: { stage: 'stellar', moment: 'wonder' },
  tonight: { stage: 'earth', moment: 'now' },
};
/** The trips each tab lists, by registry/tours.yaml `group`. Earth, the first view, lists them all. */
const TAB_GROUPS = { earth: null, planets: ['solar-system'], stars: ['beyond'], tonight: ['events'] };
/** How many trip cards before "All trips": two rows of two, which is row D's first view. */
const TRIPS_SHOWN = 4;
const COMING_UP_ROWS = 5;
/** The worlds the Planets tab lists, in order out from the Sun, the Moon after its planet. */
const PLANET_IDS = ['sun', 'mercury', 'venus', 'moon', 'mars', 'jupiter', 'saturn', 'uranus', 'neptune', 'pluto'];
/**
 * The crewed stations the "People in space" line names, by the record id their elements load as.
 * `craft` is Open Notify's own spelling (tests/fixtures/harvest/open_notify.json), the key the
 * headcount poll below matches against -- kept apart from the display label (copy/en.js R[key]),
 * which is free to read differently without breaking the match.
 */
const CREWED = [
  { id: 'sat-25544', key: 'iss', craft: 'ISS' },
  { id: 'sat-48274', key: 'tiangong', craft: 'Tiangong' },
];
const STORM_RANK = { hurricane: 3, storm: 2, depression: 1 };
const LY_PER_PC = 3.26156;
const REFRESH_MS = 30e3;
/** The aurora's Show me flight: the select flight's 900 ms (main.js flyToRecord). */
const SHOW_ME_MS = 900;
/** The card's astronomical-unit words, which the list shortens to the symbol. */
const AU_WORDS = t(COPY.card.values.au, { n: '' }).trim();

/**
 * Open Notify's headcount per craft (data/parsers.js parseAstros), read once and kept here --
 * the snapshot is minutes old at most, and `load()` already has its own cache and cadence gate,
 * so nothing here re-fetches sooner than that. Module-level, not per-instance: the data has no
 * observer, so two mounted explore views would otherwise poll twice for the same answer.
 */
let astrosCounts = null;
let astrosInFlight = null;
/** Same craft -> count map, order aside; a changed answer is the only one worth a repaint. */
function sameCounts(a, b) {
  if (a === b) return true;
  if (!a || !b) return false;
  const ak = Object.keys(a);
  if (ak.length !== Object.keys(b).length) return false;
  return ak.every((k) => a[k] === b[k]);
}
/**
 * Kicks off a read of the headcount and repaints Right now once it lands WITH A CHANGED ANSWER;
 * a no-op mid-flight, and -- since this runs from inside paintNow -- a no-op once loaded and
 * unchanged, or `onLoaded` calling paintNow would call this again forever.
 */
function pollAstros(onLoaded) {
  if (astrosInFlight) return;
  astrosInFlight = load('open-notify-astros')
    .then((res) => {
      astrosInFlight = null;
      if (!res || !res.ok || res.data == null) return;
      const byCraft = parseAstros(res.data);
      if (byCraft && !sameCounts(byCraft, astrosCounts)) { astrosCounts = byCraft; onLoaded(); }
    })
    .catch(() => { astrosInFlight = null; });
}

/** Where a tab goes. Pure. */
export function tabTarget(tab) {
  return TARGETS[tab] || TARGETS.earth;
}

/**
 * Which tab the scene is showing. Pure. The moment first -- Now is Tonight wherever the camera is --
 * then the stage: the Earth's and the Moon's are Earth, a rung of the ladder or a star system's
 * stage is Stars, and the Sun's or another world's is Planets. An unknown stage is Earth.
 */
export function tabFor(stageId, moment) {
  if (moment === 'now') return 'tonight';
  if (!stageId || stageId === 'earth' || stageId === 'moon') return 'earth';
  if (isLadderStage(stageId) || isSystemStage(stageId)) return 'stars';
  if (STAGES[stageId]) return 'planets';
  return 'earth';
}

function capitalise(s) {
  const str = String(s || '');
  return str ? str[0].toUpperCase() + str.slice(1) : str;
}

/**
 * The Right-now lines, from live state only. Pure.
 *
 * @param {{storms?: Object[], aurora?: {text, value}|null, clouds?: {mode, capturedMs}|null, crewed?: {key, record, count?: number}[], wallMs?: number}} input
 * @returns {{id: string, text: string, value?: string, lead?: boolean, record?: Object}[]} at most three
 *
 * A line whose data is missing is LEFT OUT (0061 design §2): no storms loaded is no storm line, not
 * "no storms right now", which would be a claim about the ocean made from a failed download.
 */
export function rightNowLines(input = {}) {
  const R = COPY.rightNow;
  const out = [];
  const storms = (Array.isArray(input.storms) ? input.storms : []).filter((r) => r && r.name);
  if (storms.length) {
    const strongest = storms.slice().sort((a, b) => {
      const ma = a.meta || {};
      const mb = b.meta || {};
      return ((STORM_RANK[mb.status] || 0) - (STORM_RANK[ma.status] || 0))
        || ((Number(mb.trackMaxWindKmh) || 0) - (Number(ma.trackMaxWindKmh) || 0));
    })[0];
    const n = storms.length;
    const text = n === 1
      ? t(R.stormOne, { name: strongest.name })
      : t(R.storms, { n: capitalise(COPY.numberWords[n] || fmt.int(n)), name: strongest.name });
    out.push({ id: 'storms', text, record: strongest });
  }
  // A geomagnetic storm under way (internal #192 item 4): scene/aurora.js auroraRightNow's words,
  // only at Kp 5 and above, so it is news when it is here. After the storms, which are the usual
  // lead, and before the clouds and the crew, which are there every day.
  const a = input.aurora;
  if (a && a.text) out.push({ id: 'aurora', text: a.text, value: a.value || '' });
  const c = input.clouds;
  if (c && c.mode === 'live' && Number.isFinite(c.capturedMs) && Number.isFinite(input.wallMs)) {
    out.push({ id: 'clouds', text: R.clouds, value: ageInWords(Math.max(0, input.wallMs - c.capturedMs)) });
  }
  const crewed = (Array.isArray(input.crewed) ? input.crewed : []).filter((x) => x && x.record && R[x.key]);
  if (crewed.length) {
    // The headcount (Open Notify, via explore.js's astros poll) names how many once it has
    // loaded; a station with no count yet, or Open Notify unreachable, falls back to its bare name.
    const names = crewed.map((x) => (Number.isFinite(x.count) && x.count > 0)
      ? t(R.crewAt, { n: fmt.int(x.count), name: R[x.key] })
      : R[x.key]);
    out.push({ id: 'crew', text: R.people, value: names.join(COPY.punctuation.separator), record: crewed[0].record });
  }
  const lines = out.slice(0, 3);
  if (lines.length) lines[0].lead = true;
  return lines;
}

/**
 * The status line at the foot: a dot and one sentence (design §2). Pure.
 * `rows` are data/sources.js status() rows. Only the sources this page has asked for count, as in
 * the sheet itself (ui/status.js askedRows): a source nothing has asked for has not failed.
 */
export function statusSummary(rows, opts = {}) {
  const S = COPY.statusLine;
  const asked = (Array.isArray(rows) ? rows : []).filter((r) => r && (r.attempted || r.fetchedAt != null));
  // With no network (ui/offline.js keeps ctx.net) the honest sentence is not "3 could not be read":
  // it is that nothing can be, and how old the copies on screen are (the oldest, as below).
  if (opts && opts.offline) {
    let oldestKept = null;
    for (const r of asked) {
      const age = Number(r.ageMs);
      if (Number(r.fetchedAt) > 0 && Number.isFinite(age) && (oldestKept === null || age > oldestKept)) oldestKept = age;
    }
    return oldestKept === null
      ? { state: 'failed', text: S.offlineNone }
      : { state: 'stale', text: t(S.offline, { age: ageInWords(oldestKept) }) };
  }
  if (!asked.length) return { state: 'wait', text: S.reading };
  let read = 0;
  let stale = 0;
  let failed = 0;
  let oldest = null;
  for (const r of asked) {
    const f = Number(r.fetchedAt);
    if (!Number.isFinite(f) || f <= 0) { failed += 1; continue; }
    read += 1;
    if (r.stale) stale += 1;
    const age = Number(r.ageMs);
    if (Number.isFinite(age) && (oldest === null || age > oldest)) oldest = age;
  }
  const parts = [t(read === 1 ? S.readOne : S.read, { n: fmt.int(read) })];
  if (failed) parts.push(t(S.failed, { n: fmt.int(failed) }));
  else if (stale) parts.push(t(S.stale, { n: fmt.int(stale) }));
  if (oldest !== null && !failed) parts.push(t(S.oldest, { age: ageInWords(oldest) }));
  return { state: failed ? 'failed' : stale ? 'stale' : 'ok', text: parts.join(COPY.punctuation.separator) };
}

/** The line under a trip card's title: its event's date, its shape, its reason, or "working it out". */
export function tripMeta(row, eventLine) {
  const T = COPY.tripCard;
  if (!row) return '';
  if (!row.planned) return T.planning;
  if (row.off) return row.reason || T.cannotRun;
  if (eventLine) return eventLine;
  if (row.failed || !Number.isFinite(row.count)) return '';
  const min = Math.max(1, Math.round((Number(row.estimateMs) || 0) / 60e3));
  return t(row.count === 1 ? T.metaOne : T.meta, { n: fmt.int(row.count), m: fmt.int(min) });
}

/**
 * The order the home shows its trips in (public #241). Pure. First the trip a returning visitor
 * left in the last day (`resumes(row)`: ui/passport.js says where it can be picked up), so their
 * way back in is the first card and not the ninth; then the ones that can run; last the ones that
 * cannot: the first view must not open on a greyed card.
 */
export function homeTripOrder(rows, resumes) {
  const list = Array.isArray(rows) ? rows : [];
  const mine = (r) => !r.off && typeof resumes === 'function' && !!resumes(r);
  return list.filter(mine).concat(list.filter((r) => !r.off && !mine(r)), list.filter((r) => r.off));
}

/**
 * What the home says about trips that cannot run while their cards are folded away (public #241):
 * how many, and why when they all give the same reason. '' when none is hidden. Pure.
 * `hidden` are the rows below the fold. The cards themselves, once shown, each carry their reason.
 */
export function offNote(hidden, T = COPY.tripCard) {
  const off = (Array.isArray(hidden) ? hidden : []).filter((r) => r && r.off);
  if (!off.length) return '';
  const line = off.length === 1 ? T.offOne : t(T.offMany, { n: fmt.int(off.length) });
  const reasons = new Set(off.map((r) => String(r.reason || '').trim()));
  const reason = reasons.size === 1 ? [...reasons][0] : '';
  return reason ? t(T.offWhy, { line, reason }) : line;
}

// ---------------------------------------------------------------------------------------------
// DOM
// ---------------------------------------------------------------------------------------------

const SVG_NS = 'http://www.w3.org/2000/svg';

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined && text !== null && text !== '') node.textContent = String(text);
  return node;
}

function button(className, text) {
  const b = el('button', className, text);
  b.type = 'button';
  return b;
}

function svgIcon(d, size = 18) {
  const svg = document.createElementNS(SVG_NS, 'svg');
  svg.setAttribute('viewBox', '0 0 24 24');
  svg.setAttribute('width', String(size));
  svg.setAttribute('height', String(size));
  svg.setAttribute('aria-hidden', 'true');
  svg.setAttribute('focusable', 'false');
  const p = document.createElementNS(SVG_NS, 'path');
  p.setAttribute('d', d);
  p.setAttribute('fill', 'none');
  p.setAttribute('stroke', 'currentColor');
  p.setAttribute('stroke-width', '1.75');
  p.setAttribute('stroke-linecap', 'round');
  svg.appendChild(p);
  return svg;
}

/** A list row that is a button: a name on the left, a mono value on the right. */
function rowButton(name, value, onClick) {
  const li = el('li', 'sr-list__row');
  const b = button('sr-list__btn');
  b.appendChild(el('span', 'sr-list__name', name));
  const v = el('span', 'sr-list__value', value || '');
  b.appendChild(v);
  b.addEventListener('click', onClick);
  li.appendChild(b);
  return { li, b, v };
}

function section(title, className) {
  const s = el('section', `sr-sect ${className || ''}`.trim());
  s.appendChild(el('h2', 'sr-micro', title));
  return s;
}

/** Is this element somewhere a person types? A field, an editable region, or an ARIA text box. Pure. */
export function isTypingIn(node) {
  if (!node || typeof node !== 'object') return false;
  const tag = node.tagName ? String(node.tagName).toUpperCase() : '';
  if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return true;
  if (node.isContentEditable) return true;
  const attr = (name) => (typeof node.getAttribute === 'function' ? node.getAttribute(name) : null);
  const editable = attr('contenteditable');
  if (editable !== null && editable !== undefined && String(editable).toLowerCase() !== 'false') return true;
  const role = String(attr('role') || '').toLowerCase();
  return role === 'textbox' || role === 'searchbox' || role === 'combobox' || role === 'spinbutton';
}

/**
 * Whether a key press asks for the search field. Pure, like ui/rail.js railKey: `/` alone, never
 * while something is being typed, never with a modifier (Ctrl+/ and Cmd+/ are the browser's).
 *
 * "Being typed" (public #459) is asked of BOTH the focused element and the element the key was sent
 * to: they differ when a field lives in a shadow root (document.activeElement is its host) or in a
 * frame, and a `/` in an email address must stay a `/`. Never mid-composition either: an input
 * method's `/` belongs to the word being composed.
 */
export function wantsSearch(event, activeElement) {
  if (!event || event.defaultPrevented || event.key !== '/') return false;
  if (event.ctrlKey || event.metaKey || event.altKey) return false;
  if (event.isComposing || event.keyCode === 229) return false;
  const path = typeof event.composedPath === 'function' ? event.composedPath() : [];
  const sentTo = (path && path[0]) || event.target || null;
  return !(isTypingIn(activeElement) || isTypingIn(sentTo));
}

/**
 * The generated star systems (data/systems-index.js, internal #473) as the Stars tab lists them:
 * the two groups of registry/systems-list.yaml's `why`, each nearest first, TRAPPIST-1 (typed, the
 * list's own first row) left out. Pure.
 */
export function systemGroups(index) {
  const groups = { temperate: [], extreme: [] };
  for (const s of Array.isArray(index) ? index : []) {
    if (!s || !s.hostId || !(s.why in groups)) continue;
    const pc = s.hostSky && Number(s.hostSky.distPc);
    groups[s.why].push({ id: s.id, name: s.display || s.host, hostId: s.hostId, distPc: Number.isFinite(pc) ? pc : Infinity });
  }
  for (const k of Object.keys(groups)) groups[k].sort((a, b) => a.distPc - b.distPc || (a.name < b.name ? -1 : 1));
  return groups;
}

export function createExplore(ctx, host) {
  const root = el('div', 'sr-explore');
  (host || document.body).appendChild(root);

  // --- the head: the wordmark, collapse, search, tabs -------------------------------------------
  const head = el('div', 'sr-explore__head');
  const brand = el('div', 'sr-explore__brand');
  brand.appendChild(el('p', 'sr-wordmark', COPY.app.name));
  const collapse = button('sr-explore__collapse', COPY.shell.backChevron);
  collapse.setAttribute('aria-label', COPY.shell.collapse);
  collapse.title = COPY.shell.collapse;
  collapse.addEventListener('click', () => { if (ctx.shell) ctx.shell.collapse(true); });
  brand.appendChild(collapse);
  head.appendChild(brand);

  const searchHost = el('div', 'sr-explore__search');
  searchHost.dataset.tour = 'search'; // the tour's hook (registry/onboarding.yaml): it moves with the element
  head.appendChild(searchHost);
  const search = createSearch(ctx, searchHost);
  // The magnifier inside the field, as every search box has one; the field is the label's target.
  const field = searchHost.querySelector('.sr-search__row');
  if (field) field.prepend(svgIcon('M11 4a7 7 0 1 0 0 14 7 7 0 0 0 0-14Z m9 16-4-4'));
  const input = searchHost.querySelector('.sr-search__input');
  // `/` focuses the search (docs/ui-guide.md section 3.1), from anywhere a key is not being typed:
  // the field's tooltip says so, and on a desktop a small keycap at its end shows it, as the
  // controls hint draws its keys. The cap is a picture of the key, not a control: hidden from a
  // screen reader (the tooltip and `aria-keyshortcuts` carry it) and gone while the field is used.
  if (input) {
    input.title = COPY.search.inputTitle;
    input.setAttribute('aria-keyshortcuts', COPY.search.key);
    const cap = el('kbd', 'sr-search__key', COPY.search.key);
    cap.setAttribute('aria-hidden', 'true');
    if (field) field.appendChild(cap);
  }
  // To the search field from wherever the visitor is: `/`, and the page's first skip link.
  const toSearch = (e) => {
    if (!input) return;
    const cls = document.documentElement.classList;
    // Not over a clear screen (H owns it) and not in a trip (its sheet has the sidebar).
    if (cls.contains('sr-clean') || cls.contains('sr-trip-mode')) return;
    e.preventDefault();
    const shell = ctx && ctx.shell;
    if (shell) {
      // The field lives in the home view: unfold the sidebar and come back to it. The card is left
      // by putting the selection down, as its own back row does; the sources sheet by going back.
      if (typeof shell.collapsed === 'function' && shell.collapsed()) shell.collapse(false);
      if (shell.view() === 'card' && typeof ctx.deselect === 'function') ctx.deselect();
      while (shell.view() !== 'home' && shell.stack().length > 1) shell.back();
    }
    search.focus();
  };
  document.addEventListener('keydown', (e) => { if (wantsSearch(e, document.activeElement)) toSearch(e); });
  // The skip links of index.html (public #315). "Skip to search" does what `/` does. "Skip to the
  // map" puts the focus on <main>, which takes it for that moment only: a <main> that kept a
  // tabindex would take the focus on every click of the map, and the keys would follow it there.
  const skip = (id, go) => { const a = document.getElementById(id); if (a) a.addEventListener('click', go); };
  skip('sr-skip-search', toSearch);
  skip('sr-skip-map', (e) => {
    const map = document.getElementById('map');
    if (!map) return;
    e.preventDefault();
    map.tabIndex = -1;
    map.addEventListener('blur', () => map.removeAttribute('tabindex'), { once: true });
    map.focus({ preventScroll: true });
  });
  // While a query is typed the results REPLACE the lists (design §2): one column, one answer. On a
  // phone the box is in the top bar and its results drop from it (ui/shell.js seatSearch), so the
  // flag is on the box as well, where the phone's rules read it.
  const onType = () => {
    const on = !!(input && input.value.trim().length >= 2);
    root.classList.toggle('is-searching', on);
    searchHost.classList.toggle('is-searching', on);
  };
  if (input) { input.addEventListener('input', onType); input.addEventListener('change', onType); input.addEventListener('blur', () => setTimeout(onType, 0)); }
  // The named stars and the exoplanets load after the first visit (main.js OFF THE FIRST VISIT);
  // a visitor reaching for the search box wants them now, so the first focus asks for them.
  if (input) input.addEventListener('focus', () => { if (typeof ctx.loadAfterFirstVisit === 'function') ctx.loadAfterFirstVisit(); if (typeof ctx.loadMoreCatalogues === 'function') ctx.loadMoreCatalogues(); }); // 0079 r15-a45: the catalogues of stages A4 and A5 too

  const tabs = el('div', 'sr-tabs');
  tabs.setAttribute('role', 'tablist');
  tabs.dataset.tour = 'tabs';
  tabs.setAttribute('aria-label', COPY.tabs.label);
  head.appendChild(tabs);
  root.appendChild(head);
  // The shell seats the search: here on a desktop, in the phone's top bar under 900 px.
  if (ctx && ctx.shell && typeof ctx.shell.seatSearch === 'function') ctx.shell.seatSearch(searchHost);

  const body = el('div', 'sr-explore__body');
  root.appendChild(body);
  const panes = new Map();
  const tabButtons = new Map();
  for (const id of TABS) {
    const b = button('sr-tabs__tab', COPY.tabs[id]);
    b.setAttribute('role', 'tab');
    b.id = `sr-tab-${id}`;
    b.setAttribute('aria-controls', `sr-pane-${id}`);
    b.dataset.tab = id;
    b.addEventListener('click', () => goTab(id));
    tabs.appendChild(b);
    tabButtons.set(id, b);
    const pane = el('div', 'sr-pane');
    pane.id = `sr-pane-${id}`;
    pane.setAttribute('role', 'tabpanel');
    pane.setAttribute('aria-labelledby', b.id);
    pane.hidden = true;
    body.appendChild(pane);
    panes.set(id, pane);
  }
  // Arrow keys move along the tabs and choose (the WAI-ARIA tabs pattern, automatic activation).
  tabs.addEventListener('keydown', (e) => {
    const i = TABS.indexOf(current);
    let to = null;
    if (e.key === 'ArrowRight') to = TABS[(i + 1) % TABS.length];
    else if (e.key === 'ArrowLeft') to = TABS[(i + TABS.length - 1) % TABS.length];
    else if (e.key === 'Home') to = TABS[0];
    else if (e.key === 'End') to = TABS[TABS.length - 1];
    if (!to) return;
    e.preventDefault();
    goTab(to);
    tabButtons.get(to).focus();
  });

  // --- Earth: Right now, Trips, Coming up --------------------------------------------------------
  const earth = panes.get('earth');
  const now = section(COPY.rightNow.title, 'sr-now');
  const nowList = el('ul', 'sr-now__list');
  now.appendChild(nowList);
  earth.appendChild(now);
  // TODAY (internal #270, public #450 and #395): dated cards made from what is loaded, by
  // ui/today.js, which main.js fetches after the first visit has settled. Empty and hidden until
  // then, and whenever there is nothing true to say.
  const todayHost = el('section', 'sr-sect sr-today');
  todayHost.hidden = true;
  earth.appendChild(todayHost);
  const tripHosts = new Map();
  for (const id of TABS) {
    const s = section(COPY.tripCard.title, 'sr-trips2');
    s.dataset.tour = 'trips';
    const grid = el('div', 'sr-tripgrid');
    const more = button('sr-more');
    more.hidden = true;
    // The trips one after another, hands off: a screen in a corridor (ui/autopilot.js, spec 0036).
    // "Play on its own" opens JUST WATCH: the reels by name (registry/autopilot.yaml), so a lesson's
    // worth or tonight's sky can be chosen without typing an address (internal #448). The list is
    // fetched when it is first opened: a reel is nobody's first visit.
    const own = button('sr-more', COPY.tripCard.onItsOwn);
    own.title = COPY.tripCard.onItsOwnTitle;
    own.dataset.tour = 'drift'; // the tour's hook (registry/onboarding.yaml): the drift is its first row
    own.setAttribute('aria-expanded', 'false');
    const reels = el('div', 'sr-reels');
    reels.hidden = true;
    reels.id = `sr-reels-${id}`;
    own.setAttribute('aria-controls', reels.id);
    own.addEventListener('click', () => {
      const open = reels.hidden;
      reels.hidden = !open;
      own.setAttribute('aria-expanded', String(open));
      if (!open || reels.childElementCount) return;
      Promise.all([import('../data/autopilot.js'), import('./autopilotplan.js')]).then(([data, plan]) => {
        if (reels.childElementCount) return;
        reels.appendChild(el('h3', 'sr-micro', COPY.tripCard.justWatch));
        const list = el('ul', 'sr-list');
        // The drift first (ui/drift.js, spec 0071): the one row that is not a list of trips.
        if (typeof ctx.wantDrift === 'function') {
          const { li, b } = rowButton(COPY.tripCard.drift, COPY.tripCard.driftLength, () => ctx.wantDrift({ drift: '1' }, 'row'));
          b.title = COPY.tripCard.driftTitle;
          b.dataset.reel = 'drift';
          list.appendChild(li);
        }
        for (const row of plan.reelRows(data.REELS, data.AUTOPILOT.default)) {
          const { li, b } = rowButton(row.title, t(COPY.tripCard.reelLength, { m: String(row.minutes) }), () => {
            if (typeof ctx.wantAutopilot === 'function') ctx.wantAutopilot({ ambient: row.id }, 'row');
          });
          b.title = row.blurb;
          b.dataset.reel = row.id;
          list.appendChild(li);
        }
        reels.appendChild(list);
      }).catch((err) => {
        // The list did not arrive: the button does what it always did, the default reel.
        console.warn('the reels did not load', err);
        reels.hidden = true;
        if (typeof ctx.wantAutopilot === 'function') ctx.wantAutopilot({ ambient: '1' }, 'row');
      });
    });
    const off = el('p', 'sr-sect__note sr-trips2__off');
    off.hidden = true;
    s.append(grid, off, more, own, reels);
    tripHosts.set(id, { s, grid, more, off, expanded: false });
  }
  earth.appendChild(tripHosts.get('earth').s);
  // MOMENTS (internal spec 0079 C8): the shelf of replayed missions, under the trips. Empty and hidden until
  // moments/shelf.js is fetched, after the first visit has settled, in an idle moment; never at boot.
  const momentsHost = section('', 'sr-moments');
  momentsHost.hidden = true;
  earth.appendChild(momentsHost);
  const mountMoments = () => {
    const go = () => import('../moments/shelf.js').then((m) => m.mountShelf(momentsHost, ctx)).catch((err) => console.warn('the moments shelf did not load', err));
    if (typeof window.requestIdleCallback === 'function') window.requestIdleCallback(go, { timeout: 8000 }); else setTimeout(go, 3000);
  };
  window.addEventListener('sr:layers-ready', mountMoments, { once: true });
  if (window.__srLayersReady) mountMoments();
  const next = createNext(ctx, { limit: COMING_UP_ROWS });
  next.root.classList.add('sr-sect');
  earth.appendChild(next.root);
  // Where the email row sits when there is a notifier (ui/subscribe.js): under the list it is about.
  const subscribeHost = el('div', 'sr-subscribe-host');
  next.root.appendChild(subscribeHost);

  // --- Planets: the worlds, a live distance from the Earth each -----------------------------------
  const planets = panes.get('planets');
  const worldsSect = section(COPY.explore.worldsTitle, 'sr-worlds');
  const worldsList = el('ul', 'sr-list');
  worldsSect.appendChild(worldsList);
  worldsSect.appendChild(el('p', 'sr-sect__note', COPY.explore.worldsDrawn));
  planets.appendChild(worldsSect);
  planets.appendChild(tripHosts.get('planets').s);
  const worldRows = new Map();
  for (const id of PLANET_IDS) {
    const r = rowButton('', '', () => {
      const rec = ctx.recordById(id);
      if (rec && typeof ctx.select === 'function') ctx.select(rec);
    });
    r.li.hidden = true;
    worldsList.appendChild(r.li);
    worldRows.set(id, r);
  }

  // --- Stars: the ladder's far places, the star systems, what the map draws of what is known --------
  const stars = panes.get('stars');
  const farSect = section(COPY.explore.farTitle, 'sr-far');
  const farList = el('ul', 'sr-list');
  farSect.appendChild(farList);
  stars.appendChild(farSect);
  const farRows = [];
  // ONE FLIGHT, BOTH WAYS (internal #410, public #451): from wherever the camera is on the ladder
  // out to the edge of what the map draws, or back down to the Earth, without a cut. The camera's
  // module (scene/climb.js) is fetched on the press. Off a stage the flight cannot pass through (a
  // planet's own, a star system's) the two rows are disabled, and say why.
  const flightRows = [['edge', 'toEdge'], ['home', 'toHome']].map(([key, method]) => {
    const r = rowButton(COPY.ladder[key], COPY.ladder[`${key}How`], () => {
      if (typeof ctx.wantClimb !== 'function') return;
      if (ctx.selected && ctx.selected() && typeof ctx.deselect === 'function') ctx.deselect();
      ctx.wantClimb().then((climb) => { if (climb) climb[method](); });
    });
    farList.appendChild(r.li);
    return { key, r };
  });
  for (const rung of LADDER_RUNGS) {
    if (!rung.target || !rung.target.record) continue; // the worlds are the Planets tab's
    const r = rowButton(rung.label, rung.distance, () => {
      const rec = ctx.recordById(rung.target.record);
      if (!rec) return;
      if (rec.layer && typeof ctx.isLayerOn === 'function' && !ctx.isLayerOn(rec.layer) && typeof ctx.setLayerOn === 'function') ctx.setLayerOn(rec.layer, true);
      ctx.select(rec);
    });
    r.b.title = rung.why;
    farList.appendChild(r.li);
    farRows.push({ rung, r });
  }
  const sysSect = section(COPY.explore.systemsTitle, 'sr-systems');
  const sysList = el('ul', 'sr-list');
  sysSect.appendChild(sysList);
  for (const sys of SYSTEMS) {
    const pc = sys.hostSky && Number(sys.hostSky.distPc);
    const value = Number.isFinite(pc) ? t(COPY.explore.lightYears, { n: fmt.int(Math.round(pc * LY_PER_PC)) }) : '';
    const r = rowButton(sys.host, value, () => {
      const rec = ctx.recordById(sys.hostId);
      if (rec) ctx.select(rec);
    });
    sysList.appendChild(r.li);
  }
  // THE MARKS OF PLANETS ROUND OTHER STARS ARE OFF UNTIL ASKED FOR (public #271): this row asks.
  const exoRow = rowButton(COPY.explore.exoRow, COPY.explore.exoShow, () => {
    if (typeof ctx.isLayerOn !== 'function' || typeof ctx.setLayerOn !== 'function') return;
    const on = !ctx.isLayerOn('exoplanets');
    ctx.setLayerOn('exoplanets', on);
    document.dispatchEvent(new CustomEvent('sr:layer-toggle', { detail: { id: 'exoplanets', on, handled: true, from: 'explore' } }));
    paintExo();
  });
  exoRow.b.title = COPY.explore.exoTitle;
  exoRow.b.dataset.layer = 'exoplanets';
  function paintExo() {
    const on = typeof ctx.isLayerOn === 'function' && !!ctx.isLayerOn('exoplanets');
    exoRow.v.textContent = on ? COPY.explore.exoHide : COPY.explore.exoShow;
    exoRow.b.setAttribute('aria-pressed', on ? 'true' : 'false');
  }
  sysList.insertBefore(exoRow.li, sysList.firstChild);
  // The thirty-nine more systems, once the exoplanet layer's later data has landed (internal #473).
  let moreSystems = false;
  const addSystems = () => {
    if (moreSystems) return;
    moreSystems = true;
    import('../data/systems-index.js').then((m) => {
      const groups = systemGroups(m.SYSTEM_INDEX);
      for (const [key, title] of [['temperate', COPY.explore.systemsMild], ['extreme', COPY.explore.systemsExtreme]]) {
        if (!groups[key].length) continue;
        sysSect.appendChild(el('h3', 'sr-micro', title));
        const list = el('ul', 'sr-list');
        for (const s of groups[key]) {
          const value = Number.isFinite(s.distPc) ? t(COPY.explore.lightYears, { n: fmt.int(Math.round(s.distPc * LY_PER_PC)) }) : '';
          const r = rowButton(s.name, value, () => {
            const rec = ctx.recordById(s.hostId);
            if (rec) ctx.select(rec);
          });
          list.appendChild(r.li);
        }
        sysSect.appendChild(list);
      }
    }).catch(() => { moreSystems = false; });
  };
  window.addEventListener('sr:later-layers', addSystems, { once: true });
  document.addEventListener('sr:layer-toggle', (e) => { if (e.detail && e.detail.id === 'exoplanets') paintExo(); });
  window.addEventListener('sr:moment', () => paintExo());
  stars.appendChild(sysSect);
  stars.appendChild(tripHosts.get('stars').s);
  const shows = section(COPY.ladder.weShowTitle, 'sr-weshow');
  const showList = el('ul', 'sr-list sr-list--quiet');
  const showRows = [];
  for (const row of WE_SHOW) {
    const li = el('li', 'sr-list__row sr-list__static');
    li.appendChild(el('span', 'sr-list__name', row.what));
    const v = el('span', 'sr-list__value', fmt.int(row.n));
    li.appendChild(v);
    li.title = t(COPY.ladder.weShowRow, { n: fmt.int(row.n), what: row.what, of: row.of });
    showList.appendChild(li);
    showRows.push({ row, v, li });
  }
  shows.appendChild(showList);
  stars.appendChild(shows);

  // --- Tonight: ui/tonight.js mounts its view here (mountTab below); the place is set inside it ------
  const tonight = panes.get('tonight');
  tonight.appendChild(tripHosts.get('tonight').s);

  // --- the status line ----------------------------------------------------------------------------
  const foot = button('sr-statusline');
  const dot = el('span', 'sr-statusline__dot');
  dot.setAttribute('aria-hidden', 'true');
  const footText = el('span', 'sr-statusline__text', COPY.statusLine.reading);
  foot.append(dot, footText);
  foot.setAttribute('aria-haspopup', 'dialog');
  foot.title = COPY.statusLine.open;
  foot.addEventListener('click', () => { if (ctx.shell) ctx.shell.openSources(); });
  root.appendChild(foot);

  // --- behaviour ----------------------------------------------------------------------------------
  let current = null;

  function paintTabs(id) {
    current = id;
    for (const [tid, b] of tabButtons) {
      const on = tid === id;
      b.setAttribute('aria-selected', on ? 'true' : 'false');
      b.tabIndex = on ? 0 : -1;
      b.classList.toggle('is-on', on);
      panes.get(tid).hidden = !on;
    }
    root.dataset.tab = id;
    paintPane(id);
  }

  /** Go there: the stage (through the black, as a trip's stage change does) and the moment. */
  // The tab last chosen. The switch reads it when it RUNS, not when it was asked for: the veil takes
  // 700 ms of frames (seconds on a slow device), and measured in headless Chrome, Planets then Earth
  // pressed inside that time landed on the Sun's stage with Earth lit, because the queued switch
  // carried the target of the first press.
  let wanted = null;

  function goTab(id) {
    if (!TABS.includes(id)) return;
    wanted = id;
    paintTabs(id);
    const apply = () => {
      const target = tabTarget(wanted);
      if (ctx.stage && ctx.stage.worldId !== target.stage && typeof ctx.setStage === 'function') {
        if (typeof ctx.deselect === 'function' && ctx.selected && ctx.selected()) ctx.deselect();
        ctx.setStage(target.stage);
        if (target.stage === 'sun' && typeof ctx.frameSolarSystem === 'function') ctx.frameSolarSystem();
      }
      if (ctx.moment !== target.moment && typeof ctx.setMoment === 'function') ctx.setMoment(target.moment);
    };
    const moves = ctx.stage && ctx.stage.worldId !== tabTarget(id).stage;
    // A tab that changes the stage moves the camera for the visitor: the way back is offered
    // (internal #274; main.js rememberView and offerUndo, ui/camundo.js).
    const remember = moves && typeof ctx.rememberView === 'function' && typeof ctx.offerUndo === 'function';
    if (remember) ctx.rememberView();
    const run = () => { apply(); if (remember) ctx.offerUndo(COPY.tabs[id]); };
    if (moves && ctx.veil && typeof ctx.veil.through === 'function') ctx.veil.through(run);
    else run();
  }

  /**
   * The aurora's Right-now line: NOAA's measured Kp (the reading Coming up already holds, through
   * data/events.js auroraItem: a storm under way, measured in the last six hours) in the words of
   * the aurora module. Left out until that module has loaded (main.js, OFF THE FIRST VISIT) and
   * whenever there is no storm.
   */
  function auroraNow(wallMs) {
    const a = ctx.aurora;
    if (!a || typeof a.rightNow !== 'function') return null;
    const w = typeof next.weather === 'function' ? next.weather() : null;
    const item = w ? auroraItem(w, wallMs) : null;
    if (!item || !item.now) return null;
    try { return a.rightNow(item.kp); } catch { return null; }
  }

  /**
   * Show me: the night side of the pole the aurora is best seen around (scene/aurora.js
   * auroraHemisphere says which), with the layer on and the Earth's card open, whose aurora line
   * says what the band is. From another stage the Earth's comes first, as a tab does.
   */
  function showAurora() {
    const a = ctx.aurora;
    if (!a || typeof a.showMe !== 'function') return;
    if (typeof ctx.isLayerOn === 'function' && !ctx.isLayerOn('aurora') && typeof ctx.setLayerOn === 'function') {
      ctx.setLayerOn('aurora', true);
      document.dispatchEvent(new CustomEvent('sr:layer-toggle', { detail: { id: 'aurora', on: true, handled: true, from: 'explore' } }));
    }
    if (ctx.stage && ctx.stage.worldId !== 'earth' && typeof ctx.setStage === 'function') ctx.setStage('earth');
    const earthRec = typeof ctx.recordById === 'function' ? ctx.recordById('earth') : null;
    if (earthRec) ctx.select(earthRec, { fly: false });
    const o = ctx.observer;
    const view = a.showMe({ observerLatDeg: o && Number.isFinite(o.latRad) ? (o.latRad * 180) / Math.PI : NaN });
    if (view && ctx.cameraRig) {
      ctx.cameraRig.flyTo({ targetScene: view.target, distance: view.distance, azimuth: view.azimuth, polar: view.polar, ms: SHOW_ME_MS });
    }
  }

  function paintNow() {
    const wallMs = Date.now();
    let clouds = null;
    try { clouds = ctx.liveClouds ? ctx.liveClouds.state() : null; } catch { clouds = null; }
    pollAstros(() => { if (current === 'earth') paintNow(); });
    const crewed = [];
    for (const c of CREWED) {
      const rec = typeof ctx.recordById === 'function' ? ctx.recordById(c.id) : null;
      if (rec) crewed.push({ key: c.key, record: rec, count: astrosCounts ? astrosCounts[c.craft] : undefined });
    }
    const lines = rightNowLines({ storms: ctx.recordsFor ? ctx.recordsFor('storms') : [], aurora: auroraNow(wallMs), clouds, crewed, wallMs });
    while (nowList.firstChild) nowList.removeChild(nowList.firstChild);
    now.hidden = !lines.length;
    for (const line of lines) {
      const li = el('li', `sr-now__row${line.lead ? ' is-lead' : ''}`);
      const b = button('sr-now__btn');
      b.appendChild(el('span', 'sr-now__text', line.text));
      if (line.value) b.appendChild(el('span', 'sr-now__value', line.value));
      if (line.id === 'aurora') {
        b.title = COPY.aurora.showMe;
        b.addEventListener('click', showAurora);
        li.appendChild(b);
        nowList.appendChild(li);
        continue;
      }
      b.addEventListener('click', () => {
        const rec = line.record || (line.id === 'clouds' ? ctx.recordById('earth') : null);
        if (!rec) return;
        if (rec.layer && typeof ctx.isLayerOn === 'function' && !ctx.isLayerOn(rec.layer) && typeof ctx.setLayerOn === 'function') {
          ctx.setLayerOn(rec.layer, true);
          document.dispatchEvent(new CustomEvent('sr:layer-toggle', { detail: { id: rec.layer, on: true, handled: true, from: 'explore' } }));
        }
        ctx.select(rec);
      });
      li.appendChild(b);
      nowList.appendChild(li);
    }
  }

  // Trips: the picker's own planning, drawn as cards.
  const trip = ctx.trip;
  // The index until ui/trip.js is here, the trips themselves after (ui/tripgate.js): read again
  // when `sr:trips-loaded` says so, below.
  const toursNow = () => (trip && typeof trip.tours === 'function' ? trip.tours() : []);
  let tours = toursNow();
  // A card whose trip has not been planned yet says what the registry gives it -- its stops and
  // its minutes -- rather than "working it out": planning waits for ui/trip.js, which is not in the
  // first visit (internal #405), and the registry's numbers are what most plans come back with.
  const unplannedShape = (row, tour) => {
    if (row.planned || !tour) return row;
    const count = Number.isFinite(tour.count) ? tour.count : Array.isArray(tour.stops) ? tour.stops.length : NaN;
    return Number.isFinite(count) ? { ...row, planned: true, count, estimateMs: tour.estimate_ms } : row;
  };
  const plans = new Map();
  // The trip a card was just pressed for, until it has started or said why not. The first press of
  // a visit has ui/trip.js to fetch and the trip's stops to find among catalogues that are still
  // landing: MEASURED 2026-10-06, a card pressed two seconds into a first visit opened its intro
  // ten seconds later, and for those ten seconds nothing on the screen had changed (the stand-in,
  // ui/tripgate.js, has no face). So the card says so itself, at once, and through every repaint.
  let startingId = null;
  const starting = (id, on) => {
    if (on) startingId = id; else if (startingId === id) startingId = null; else return;
    paintTrips(current);
  };
  function paintTrips(id) {
    const hostT = tripHosts.get(id);
    if (!hostT) return;
    const groups = TAB_GROUPS[id];
    const drawn = groupTrips(tours, TOUR_GROUPS, plans);
    let rows = [];
    for (const g of drawn) if (!groups || groups.includes(g.group)) for (const r of g.trips) rows.push({ ...r, group: g.group });
    // A trip left in the last day first, then the ones that can run, then the rest (homeTripOrder).
    rows = homeTripOrder(rows, (r) => (ctx.passport ? ctx.passport.resume(r.id) : null));
    hostT.s.hidden = !rows.length;
    const shown = hostT.expanded ? rows : rows.slice(0, TRIPS_SHOWN);
    // The cards are rebuilt as plans land, and a rebuilt card is a new node: the one a keyboard
    // visitor is on (or that leaving a trip gave focus back to) would drop focus to <body>.
    const active = typeof document !== 'undefined' ? document.activeElement : null;
    const focusedTrip = active && hostT.grid.contains(active) && active.dataset ? active.dataset.trip : null;
    while (hostT.grid.firstChild) hostT.grid.removeChild(hostT.grid.firstChild);
    const nowMs = ctx.clock && typeof ctx.clock.now === 'function' ? ctx.clock.now() : Date.now();
    for (const row of shown) {
      const tour = tours.find((x) => x.id === row.id);
      const card = button('sr-tripcard');
      card.dataset.trip = row.id;
      card.dataset.group = row.group || '';
      // Spec 0068: the trip's own picture under the glass, fading out under the title.
      card.appendChild(tripPicture(row.id, 'sr-tripcard__pic'));
      card.appendChild(el('span', 'sr-tripcard__title', row.title));
      const isStarting = startingId === row.id && !row.off;
      // A trip left in the last 24 hours says where it can be picked up (ui/passport.js, once it
      // is here): the card then starts the trip at that stop.
      const resume = !row.off && ctx.passport ? ctx.passport.resume(row.id) : null;
      const meta = isStarting ? COPY.tripCard.starting : resume ? resume.text : tripMeta(unplannedShape(row, tour), eventSubtitle(tour, nowMs, ctx.observer || null));
      card.appendChild(el('span', 'sr-tripcard__meta', meta));
      if (isStarting) { card.classList.add('is-starting'); card.setAttribute('aria-busy', 'true'); }
      if (row.off) { card.classList.add('is-off'); card.setAttribute('aria-disabled', 'true'); }
      card.title = [row.blurb, row.off ? row.reason : ''].filter(Boolean).join(' ');
      card.addEventListener('click', () => {
        if (row.off) return; // greyed WITH its reason (spec 0025 req 6), never started into a refusal
        if (startingId === row.id) return; // pressed again while it is on its way: once is enough
        const go = () => { try { return trip.start(row.id); } catch { return null; /* the trip says why itself */ } };
        // Still waiting for its stars (waitsForLater): they first, then the trip.
        const begun = !row.planned && laterPending() ? ctx.loadAfterFirstVisit().then(go) : go();
        const done = () => { starting(row.id, false); if (resume && trip.state.tourId === row.id) trip.jumpTo(resume.index); };
        if (begun && typeof begun.then === 'function') { starting(row.id, true); begun.then(done, done); } else if (resume) done();
      });
      hostT.grid.appendChild(card);
      if (focusedTrip && row.id === focusedTrip) card.focus({ preventScroll: true });
    }
    hostT.more.hidden = rows.length <= TRIPS_SHOWN;
    // The ones that cannot run are below the fold: say there are some, and why (public #241).
    const note = hostT.expanded ? '' : offNote(rows.slice(TRIPS_SHOWN));
    hostT.off.textContent = note;
    hostT.off.title = note;
    hostT.off.hidden = !note;
    hostT.more.textContent = hostT.expanded ? COPY.tripCard.fewer : t(COPY.tripCard.all, { n: fmt.int(rows.length) });
    hostT.more.setAttribute('aria-expanded', hostT.expanded ? 'true' : 'false');
  }
  for (const [id, hostT] of tripHosts) {
    hostT.more.addEventListener('click', () => { hostT.expanded = !hostT.expanded; paintTrips(id); });
  }
  // OFF THE FIRST VISIT (main.js, internal #188): the named stars and the exoplanets land a few
  // seconds after the rest. A trip with a stop the map does not hold yet, while they are still to
  // come, waits for them ("working it out") instead of being planned without them and greyed.
  const laterPending = () => typeof ctx.laterLayersLoaded === 'function' && typeof ctx.loadAfterFirstVisit === 'function' && !ctx.laterLayersLoaded();
  const waitsForLater = (tour) => laterPending()
    && (Array.isArray(tour.stops) ? tour.stops : []).some((s) => s && s.record && !ctx.recordById(s.record));
  const waiting = new Set();
  function planAll(only) {
    // Planning needs the stops and the trip's own resolver: not before ui/trip.js is here.
    if (trip.loaded === false) return;
    for (const tour of tours) {
      if (only && !only(tour)) continue;
      if (waitsForLater(tour)) { waiting.add(tour.id); continue; }
      waiting.delete(tour.id);
      Promise.resolve(trip.plan(tour.id))
        .then((p) => { if (p) { plans.set(tour.id, p); paintTrips(current); } })
        .catch(() => { plans.set(tour.id, { offerable: true, failed: true }); paintTrips(current); });
    }
  }
  if (tours.length) {
    window.addEventListener('sr:layers-ready', () => planAll(), { once: true });
    if (window.__srLayersReady) planAll();
    window.addEventListener('sr:trips-loaded', () => { tours = toursNow(); if (window.__srLayersReady) planAll(); });
    window.addEventListener('sr:later-layers', () => { if (waiting.size) planAll((tour) => waiting.has(tour.id)); });
    // A trip from your own place re-plans when the place changes (spec 0038), and only those.
    window.addEventListener('sr:observer', () => { if (plans.size) planAll((tour) => tour.requires_observer); });
  }

  function paintWorlds() {
    for (const [id, r] of worldRows) {
      const rec = typeof ctx.recordById === 'function' ? ctx.recordById(id) : null;
      r.li.hidden = !rec;
      if (!rec) continue;
      const name = rec.name || id;
      if (r.b.firstChild.textContent !== name) r.b.firstChild.textContent = name;
      let value = '';
      try {
        const lines = tagLines(rec, ctx);
        const d = lines && lines.readouts.find((x) => x.key === 'earth');
        // The card's own number, in the list's short unit: "1.52 AU" where the card says it in words.
        value = d ? (d.unit === AU_WORDS ? t(COPY.explore.au, { n: d.num }) : `${d.num} ${d.unit}`) : '';
      } catch { value = ''; }
      if (r.v.textContent !== value) r.v.textContent = value;
    }
  }

  function paintStars() {
    const id = ctx.stage && ctx.stage.worldId;
    const onChain = id === 'earth' || id === 'sun' || isLadderStage(id);
    for (const { key, r } of flightRows) {
      const ok = onChain && typeof ctx.wantClimb === 'function';
      r.b.disabled = !ok;
      r.b.title = ok ? COPY.ladder[`${key}Title`] : COPY.ladder.flightOff;
    }
    for (const { rung, r } of farRows) {
      const ok = !!(typeof ctx.recordById === 'function' && ctx.recordById(rung.target.record));
      r.b.disabled = !ok;
      r.b.setAttribute('aria-label', ok ? `${rung.label}, ${rung.distance}` : `${rung.label}, ${COPY.ladder.notLoaded}`);
    }
    paintExo();
    // A row that names its layer prints what that layer holds, so it agrees with What to show.
    for (const { row, v } of showRows) {
      if (!row.layer) continue;
      const n = (ctx.recordsFor ? ctx.recordsFor(row.layer) : []).length;
      if (n > 0) v.textContent = fmt.int(n);
    }
  }

  function paintStatus() {
    let rows = [];
    try { rows = ctx.sources && typeof ctx.sources.status === 'function' ? ctx.sources.status() : []; } catch { rows = []; }
    const offline = (typeof navigator !== 'undefined' && navigator.onLine === false) || !!(ctx.net && ctx.net.offline);
    const s = statusSummary(rows, { offline });
    if (footText.textContent !== s.text) footText.textContent = s.text;
    foot.dataset.state = s.state;
    foot.setAttribute('aria-label', t(COPY.statusLine.label, { text: s.text }));
  }

  const mounts = new Map();

  function paintPane(id) {
    if (mounts.has(id)) mounts.get(id)();
    if (id === 'earth') paintNow();
    if (id === 'planets') paintWorlds();
    if (id === 'stars') paintStars();
    paintTrips(id);
  }

  function refresh() {
    if (current) paintPane(current);
    paintStatus();
  }

  const follow = () => {
    const id = tabFor(ctx.stage && ctx.stage.worldId, ctx.moment);
    if (id !== current) paintTabs(id);
  };
  window.addEventListener('sr:stage', follow);
  window.addEventListener('sr:moment', (e) => {
    follow();
    // A link to the old Next door lands on Earth with "Coming up" in view (the moment's answer).
    if (e && e.detail === 'next') revealInColumn(next.root, 8);
  });
  window.addEventListener('sr:layer', () => refresh());
  window.addEventListener('sr:passport', () => paintTrips(current));
  window.addEventListener('sr:clouds', () => { if (current === 'earth') paintNow(); });
  // The aurora module arrived, or a forecast did: the aurora line can be written.
  window.addEventListener('sr:aurora', () => { if (current === 'earth') paintNow(); });
  // The network went, or came back (ui/offline.js; the browser's own events before that module lands).
  for (const name of ['sr:net', 'online', 'offline']) window.addEventListener(name, () => paintStatus());
  setInterval(refresh, REFRESH_MS);
  // The status line settles over the first seconds as the sources answer; a quicker look then.
  let early = 0;
  const earlyTimer = setInterval(() => { paintStatus(); if (++early > 20) clearInterval(earlyTimer); }, 1500);

  paintTabs(tabFor(ctx.stage && ctx.stage.worldId, ctx.moment));
  paintStatus();

  /**
   * A tab's content from another module: `render(host, ctx)` fills a section at the top of the tab's
   * pane, once, the first time the tab is shown (or at once if it is showing). With `replace: true`
   * the tab's own sections are set aside and only its trips stay under the mounted content. The
   * Tonight tab is the first user: spec 0051's ui/tonight.js renderTonight(host, ctx) takes the old
   * Now moment's place list over with
   *     ctx.explore.mountTab('tonight', renderTonight, { replace: true })
   * and nothing in this file changes when it lands.
   */
  function mountTab(id, render, opts = {}) {
    const pane = panes.get(id);
    if (!pane || typeof render !== 'function') return null;
    const mount = el('section', 'sr-sect sr-pane__mount');
    pane.prepend(mount);
    if (opts.replace) {
      for (const child of [...pane.children]) {
        if (child !== mount && child !== tripHosts.get(id).s) child.hidden = true;
      }
    }
    let done = false;
    const run = () => {
      if (done) return;
      done = true;
      try { render(mount, ctx); } catch (e) { console.warn(`the ${id} tab's content did not render`, e); }
    };
    mounts.set(id, run);
    if (current === id) run();
    return mount;
  }

  // `crew` is Open Notify's headcount by craft, or null until it is read (ui/sentence.js).
  const api = { root, tab: () => current, setTab: goTab, refresh, search, subscribeHost, next, todayHost, crew: () => astrosCounts, mountTab };
  if (ctx) ctx.explore = api;
  return api;
}
